<template>
  <div class="readiness-container-new-readiness">
    <ReadinessPage
      :menuItems="menuItems"
      :initialTab="initialTab"
      @tab-selected="selectTab"
      :averageReadiness="averageReadiness"
      :last_month_averageReadiness="last_month_averageReadiness"
      :employees="employees"
      :filterGroups="filterGroups"
      :appliedFilters="appliedFiltersAsLabels"
      :searchText="searchText"
      :totalCount="totalCount"
      :currentPage="currentPage"
      :itemsPerPage="itemsPerPage"
      :toggleLoader="readinessLoading"
      @onChatterClick="handleChatterClick"
      @onSearch="handleSearch"
      @onFilterChange="handleFilterChange"
      @onRemoveFilter="handleRemoveFilter"
      @onClearFilters="handleClearFilters"
      @onExcludeChange="handleExcludeChange"
      @onPaginationChange="handlePaginationChange"
    />
    <ChecklistDrawer
      :isEmployeeChatter="true"
      ref="drawerContainer"
      :drawer="isDrawerOpen"
      :avatar="{
        picture: currentEmployee?.avatar,
        firstName: currentEmployee?.first_name || '',
        lastName: currentEmployee?.last_name || '',
      }"
      :typeValue="
        (currentEmployee?.first_name || '') +
        ' ' +
        (currentEmployee?.last_name || '')
      "
      @closeDrawer="closeEmployeeChatterModal"
      :type="'Employee'"
      :chatterList="chatterData"
      :totalCheckpointChatter="totalEmployeeChatter"
      @requestMoreCheckpointsChatter="requestMoreChatter"
    />
  </div>
</template>

<script>
import { onMounted, computed, getCurrentInstance, ref } from "vue";
import Employee from "@components/assets/icons/Employee.svg";
import ReadinessPage from "@components/ReadinessPage/ReadinessPage.vue";
import ChecklistDrawer from "@components/Drawer/ChatterDrawer.vue";
import { useReadinessEmployees } from "@/composables/readinessEmployees/useReadinessEmployees";
import { useEmployees } from "@/composables/employees/useEmployees";
import { transformChatterData } from "@/utils/chatterUtils";
import { getChatterList } from "@/api";
import { useFilters } from "@/composables/filters/useFilters";

export default {
  components: {
    ReadinessPage,
    ChecklistDrawer,
  },
  setup() {
    const app = getCurrentInstance();
    const { proxy } = app;
    const employees = useReadinessEmployees();
    const employeeService = useEmployees();
    const filter = useFilters();

    // Load saved filters from localStorage
    const savedFilters = filter.getFilters("readinessEmployees");

    // Clean up saved filters: remove "no_filter" if "requires_recalc" is present
    const cleanedSavedFilters = savedFilters
      ? savedFilters.includes("requires_recalc")
        ? savedFilters.filter((item) => item !== "no_filter")
        : savedFilters
      : [];

    const baseMenuItems = [
      {
        label: "Employees",
        icon: Employee,
      },
    ];

    const averageReadiness = ref(0);
    const last_month_averageReadiness = ref(null);
    const initialTab = ref("Employees");
    const currentEmployee = ref(null);
    const isDrawerOpen = ref(false);
    const chatterData = ref([]);
    const totalEmployeeChatter = ref(0);
    const chatterPage = ref(1);
    const searchText = ref("");
    const appliedFilters = ref(cleanedSavedFilters);
    const readinessLoading = ref(false);
    const filterGroups = ref([
      {
        label: "Recalculation",
        items: [
          { label: "No Filter", value: "no_filter" },
          { label: "Requires Recalc", value: "requires_recalc" },
        ],
      },
    ]);

    const totalCount = computed(() => {
      return employees.employeeStore.getTotalEmployeesCount || 0;
    });

    const currentPage = computed(() => {
      return employees.employeeStore.getPagination.currentPage || 1;
    });

    const itemsPerPage = computed(() => {
      return employees.employeeStore.getPagination.itemsPerPage || 10;
    });

    const resetPagination = () => {
      if (employees.employeeStore) {
        employees.employeeStore.setPagination({
          currentPage: 1,
          itemsPerPage: 10,
          totalCount: employees.employeeStore.getTotalEmployeesCount,
        });
      }
    };

    const loadEmployeesWithFilters = async () => {
      // Update the employee store's pagination state to match our viewState BEFORE making API call
      const employeeStore = employees.employeeStore;
      if (employeeStore) {
        employeeStore.setPagination({
          currentPage: employeeStore.getPagination.currentPage || 1,
          itemsPerPage: employeeStore.getPagination.itemsPerPage || 10,
          totalCount: employeeStore.getTotalEmployeesCount,
        });
      }

      const queryParams = {
        limit: employeeStore?.getPagination?.itemsPerPage || 10,
        offset:
          ((employeeStore?.getPagination?.currentPage || 1) - 1) *
          (employeeStore?.getPagination?.itemsPerPage || 10),
        ordering: "first_name",
        status: "active",
        archived: false,
      };

      // Add search parameter
      if (searchText.value && searchText.value.trim()) {
        queryParams.name = searchText.value.trim();
      }

      // Add filter parameters
      // Apply needs_recalc filter if "requires_recalc" is in filters
      // Note: "no_filter" should already be excluded in handleFilterChange, but double-check here
      const effectiveFilters = appliedFilters.value.filter(
        (item) => item !== "no_filter"
      );
      if (effectiveFilters.includes("requires_recalc")) {
        queryParams.needs_recalc = true;
      }

      await employees.loadAllEmployees(queryParams);
    };

    const loadAccountDetails = async () => {
      readinessLoading.value = true;
      try {
        const accountID = JSON.parse(localStorage.getItem("currentAccountID"));
        const response = await employeeService.getAccountDetails(accountID);
        if (response) {
          // Add setTimeout to match readiness employee view behavior
          setTimeout(() => {
            averageReadiness.value = response.data.avg_employee_readiness || 0;
            last_month_averageReadiness.value =
              response.data.last_month_avg_employee_readiness || null;
            readinessLoading.value = false;
          }, 1500);
        } else {
          readinessLoading.value = false;
        }
      } catch (e) {
        console.error("Error loading account details: ", e);
        readinessLoading.value = false;
      }
    };

    onMounted(async () => {
      try {
        proxy.$loader.show();
        // Initialize pagination state
        resetPagination();
        await loadEmployeesWithFilters();
        await loadAccountDetails();
      } catch (e) {
        console.error("Error loading data: ", e);
      } finally {
        proxy.$loader.hide();
      }
    });

    const activeEmployeesCount = computed(() => {
      return employees.employeeStore.getTotalEmployeesCount || 0;
    });

    const menuItems = computed(() => {
      return (baseMenuItems || []).map((item) => {
        if (item?.label === "Employees") {
          return { ...item, badgeCount: activeEmployeesCount.value };
        }
        return item;
      });
    });

    const selectTab = (payload) => {
      const tab = typeof payload === "string" ? payload : payload?.label;
      if (!tab) return;
      initialTab.value = tab;
    };

    const getEmployeeChatter = async () => {
      try {
        if (!currentEmployee.value?.id) return;

        const accountID = JSON.parse(localStorage.getItem("currentAccountID"));
        const response = await getChatterList(
          accountID,
          currentEmployee.value.id,
          "employee",
          chatterPage.value
        );

        if (response?.data) {
          const chatterItems = transformChatterData(response.data.results);
          chatterData.value.push(...chatterItems);
          totalEmployeeChatter.value = response.data.count;
        }
      } catch (error) {
        console.error("Error loading employee chatter: ", error);
      }
    };

    const handleChatterClick = async (employeeData) => {
      try {
        // Set current employee
        currentEmployee.value = employeeData;
        employeeService.setCurrentEmployee(employeeData);

        // Reset chatter state
        chatterData.value = [];
        chatterPage.value = 1;
        isDrawerOpen.value = true;

        // Load chatter data
        await getEmployeeChatter();
      } catch (error) {
        console.error("Error opening chatter modal: ", error);
      }
    };

    const closeEmployeeChatterModal = () => {
      chatterData.value = [];
      totalEmployeeChatter.value = 0;
      chatterPage.value = 1;
      isDrawerOpen.value = false;
      currentEmployee.value = null;
    };

    const requestMoreChatter = async () => {
      chatterPage.value = Math.ceil(chatterData.value.length / 10) + 1;
      await getEmployeeChatter();
    };

    // Debounce function for search
    const debounce = (func, delay) => {
      let timeout;
      return function (...args) {
        const context = this;
        clearTimeout(timeout);
        timeout = setTimeout(() => func.apply(context, args), delay);
      };
    };

    // Perform search with proper filtering
    const performSearch = async (searchTextValue) => {
      // Clear previous search
      if (searchText.value) {
        searchText.value = "";
      }

      // Only add search filters and load employees if there's actually a search term
      if (searchTextValue && searchTextValue.trim() !== "") {
        searchText.value = searchTextValue.trim();
      }

      // Reset to first page when searching
      resetPagination();

      // Load employees with current filters (including search)
      await loadEmployeesWithFilters();
    };

    // Debounced search function
    const debouncedSearch = debounce(performSearch, 500);

    // Handle search input
    const handleSearch = (text) => {
      debouncedSearch(text);
    };

    // Handle filter change
    const handleFilterChange = async (filters) => {
      // Create maps for label/value conversion
      const labelToValueMap = {};
      const valueToLabelMap = {};
      filterGroups.value.forEach((group) => {
        group.items.forEach((item) => {
          labelToValueMap[item.label] = item.value;
          valueToLabelMap[item.value] = item.label;
        });
      });

      const newArray = filters
        .map((item) => {
          // Handle string values (could be label or value)
          if (typeof item === "string") {
            // If it's a value (like "requires_recalc"), return it
            if (valueToLabelMap[item]) {
              return item;
            }
            // If it's a label (like "Requires Recalc"), map to value
            return labelToValueMap[item] || item;
          }
          // Handle object values from RadioInput
          if (item && typeof item === "object") {
            // If value is null or undefined, skip it (e.g., "No Filter" deselected)
            if (item.value === null || item.value === undefined) {
              return null;
            }
            // RadioInput emits labels, so map label to value
            if (Object.prototype.hasOwnProperty.call(item, "value")) {
              const filterValue = labelToValueMap[item.value] || item.value;
              return filterValue;
            }
          }
          return null;
        })
        .filter(Boolean);

      // Remove "no_filter" if "requires_recalc" is selected (they are mutually exclusive)
      const filteredArray = newArray.includes("requires_recalc")
        ? newArray.filter((item) => item !== "no_filter")
        : newArray;

      filter.setFilters({ key: "readinessEmployees", filters: filteredArray });
      appliedFilters.value = filteredArray;

      // Reset pagination when filters are applied
      resetPagination();
      await loadEmployeesWithFilters();
    };

    // Handle remove filter
    const handleRemoveFilter = async (removedFilter) => {
      // Create maps for label/value conversion
      const labelToValueMap = {};
      const valueToLabelMap = {};
      filterGroups.value.forEach((group) => {
        group.items.forEach((item) => {
          labelToValueMap[item.label] = item.value;
          valueToLabelMap[item.value] = item.label;
        });
      });

      // Convert removedFilter (which is a label from chip) to value
      const filterValueToRemove =
        labelToValueMap[removedFilter] || removedFilter;

      // Remove the filter from appliedFilters
      const updatedFilters = appliedFilters.value.filter(
        (filterValue) => filterValue !== filterValueToRemove
      );

      appliedFilters.value = updatedFilters;
      filter.setFilters({
        key: "readinessEmployees",
        filters: updatedFilters,
      });

      // Reset pagination and reload employees
      resetPagination();
      await loadEmployeesWithFilters();
    };

    // Handle clear filters
    const handleClearFilters = async () => {
      appliedFilters.value = [];
      searchText.value = "";
      filter.setFilters({ key: "readinessEmployees", filters: [] });
      resetPagination();
      await loadEmployeesWithFilters();
    };

    // Handle exclude from average change
    const handleExcludeChange = async ({ employee, value }) => {
      console.log("handleExcludeChange called:", { employee, value });
      if (!employee || !employee.id) {
        console.error("Invalid employee data in handleExcludeChange");
        return;
      }
      try {
        proxy.$loader.show();
        readinessLoading.value = true;

        // Get the actual current page from the store to preserve pagination
        const employeeStore = employees.employeeStore;
        const actualCurrentPage =
          employeeStore?.getPagination?.currentPage || 1;
        const actualItemsPerPage =
          employeeStore?.getPagination?.itemsPerPage || 10;

        // Update pagination state to match store before making API call
        if (employeeStore) {
          employeeStore.setPagination({
            currentPage: actualCurrentPage,
            itemsPerPage: actualItemsPerPage,
            totalCount: employeeStore.getTotalEmployeesCount,
          });
        }

        const accountID = JSON.parse(localStorage.getItem("currentAccountID"));
        const payload = {
          emp_id: employee.id,
          value: value.value,
        };
        await employeeService.updateEmployeeExcludeFromAvg(accountID, payload);

        // Update pagination state again before loading to preserve current page
        if (employeeStore) {
          employeeStore.setPagination({
            currentPage: actualCurrentPage,
            itemsPerPage: actualItemsPerPage,
            totalCount: employeeStore.getTotalEmployeesCount,
          });
        }

        // Reload employees with current filters to preserve query parameters
        await loadEmployeesWithFilters();
        // Load account details to update average readiness
        await loadAccountDetails();
      } catch (error) {
        console.error("Error updating exclude from average: ", error);
        readinessLoading.value = false;
      } finally {
        proxy.$loader.hide();
      }
    };

    // Handle pagination change
    const handlePaginationChange = async (paginationData) => {
      try {
        proxy.$loader.show();
        // Update pagination state in store
        if (employees.employeeStore) {
          employees.employeeStore.setPagination({
            currentPage: paginationData.page,
            itemsPerPage: paginationData.itemsPerPage || itemsPerPage.value,
            totalCount: totalCount.value,
          });
        }
        // Load employees with current filters to preserve query parameters
        await loadEmployeesWithFilters();
      } catch (error) {
        console.error("Error handling pagination change: ", error);
      } finally {
        proxy.$loader.hide();
      }
    };

    // Convert filter values to labels for SearchModal (it expects labels in defaultSelected)
    const appliedFiltersAsLabels = computed(() => {
      const valueToLabelMap = {};
      filterGroups.value.forEach((group) => {
        group.items.forEach((item) => {
          valueToLabelMap[item.value] = item.label;
        });
      });
      return (appliedFilters.value || []).map(
        (value) => valueToLabelMap[value] || value
      );
    });

    // Filter employees on frontend based on applied filters
    const filteredEmployees = computed(() => {
      let allEmployees = employees.allEmployees.value || [];

      // Apply needs_recalc filter on frontend if "requires_recalc" is in filters
      const effectiveFilters = (appliedFilters.value || []).filter(
        (item) => item !== "no_filter" && item !== null && item !== undefined
      );

      if (effectiveFilters.includes("requires_recalc")) {
        allEmployees = allEmployees.filter(
          (employee) => employee.needs_recalc === true
        );
      }

      return allEmployees;
    });

    return {
      menuItems,
      averageReadiness,
      last_month_averageReadiness,
      employees: filteredEmployees,
      initialTab,
      selectTab,
      handleChatterClick,
      closeEmployeeChatterModal,
      requestMoreChatter,
      isDrawerOpen,
      currentEmployee,
      chatterData,
      totalEmployeeChatter,
      filterGroups,
      appliedFilters,
      appliedFiltersAsLabels,
      searchText,
      handleSearch,
      handleFilterChange,
      handleRemoveFilter,
      handleClearFilters,
      handleExcludeChange,
      handlePaginationChange,
      totalCount,
      currentPage,
      itemsPerPage,
      readinessLoading,
    };
  },
};
</script>

<style lang="css" scoped>
.readiness-container-new-readiness {
  margin: 15px;
  width: 100%;
}
</style>
